import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, Building2, FileText, User } from 'lucide-react';

const CompanyOnboarding = () => {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    website_url: '',
    industry: '',
    size: '',
    stage: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_role: ''
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const steps = [
    { icon: Building2, title: 'Company Info', description: 'Basic details' },
    { icon: FileText, title: 'Description', description: 'About your company' },
    { icon: User, title: 'Contact', description: 'Primary contact' }
  ];

  const handleNext = () => {
    if (step === 1 && (!formData.name || !formData.website_url || !formData.industry || !formData.size || !formData.stage)) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (step === 2 && !formData.description) {
      toast.error('Please add a company description');
      return;
    }
    if (step === 3 && (!formData.contact_name || !formData.contact_email || !formData.contact_role)) {
      toast.error('Please fill in all contact details');
      return;
    }

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/company/profile`, formData);
      await refreshProfile();
      toast.success('Company registered successfully! Your profile will be reviewed.');
      navigate('/company/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-mesh py-12" data-testid="company-onboarding">
      <div className="container-custom max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <h1 className="heading-2 mb-2">Register Your Company</h1>
          <p className="text-slate-600">Let's get your company set up on Talent Collective</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <Progress value={progress} className="h-2 mb-4" />
          <div className="flex justify-between">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i + 1 === step;
              const isComplete = i + 1 < step;
              return (
                <div key={i} className={`flex flex-col items-center ${i + 1 <= step ? 'text-accent' : 'text-slate-300'}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center mb-1 ${
                    isComplete ? 'bg-accent text-white' : 
                    isActive ? 'bg-accent/10 text-accent border-2 border-accent' : 
                    'bg-slate-100 text-slate-400'
                  }`}>
                    {isComplete ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{s.title}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <Card className="p-8" data-testid="onboarding-form">
          {/* Step 1: Company Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="heading-3 mb-2">Company Information</h2>
                <p className="text-slate-600">Tell us about your company</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    placeholder="Acme Inc."
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website URL *</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://acme.com"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    data-testid="website-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Input
                    id="industry"
                    placeholder="e.g., SaaS, Fintech, Healthcare"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    data-testid="industry-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Size *</Label>
                    <Select value={formData.size} onValueChange={(v) => setFormData({ ...formData, size: v })}>
                      <SelectTrigger data-testid="size-select">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 employees</SelectItem>
                        <SelectItem value="11-50">11-50 employees</SelectItem>
                        <SelectItem value="51-200">51-200 employees</SelectItem>
                        <SelectItem value="201-500">201-500 employees</SelectItem>
                        <SelectItem value="500+">500+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Stage *</Label>
                    <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                      <SelectTrigger data-testid="stage-select">
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seed">Seed</SelectItem>
                        <SelectItem value="series_a">Series A</SelectItem>
                        <SelectItem value="series_b">Series B</SelectItem>
                        <SelectItem value="series_c">Series C</SelectItem>
                        <SelectItem value="growth">Growth</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Description */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="heading-3 mb-2">About Your Company</h2>
                <p className="text-slate-600">Help talent understand what you're building</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="description">Company Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell talent about your company, mission, and what you're building..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="min-h-[200px]"
                    data-testid="description-input"
                  />
                  <p className="text-xs text-slate-500">This will be shown to talent when they view your projects</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Contact */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="heading-3 mb-2">Primary Contact</h2>
                <p className="text-slate-600">Who should we contact about your account?</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contactName">Full Name *</Label>
                  <Input
                    id="contactName"
                    placeholder="Sarah Chen"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="sarah@acme.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactRole">Role *</Label>
                  <Input
                    id="contactRole"
                    placeholder="e.g., Head of Talent, CEO"
                    value={formData.contact_role}
                    onChange={(e) => setFormData({ ...formData, contact_role: e.target.value })}
                    data-testid="contact-role-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
            <Button variant="ghost" onClick={handleBack} disabled={step === 1} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <Button className="btn-accent" onClick={handleNext} disabled={loading} data-testid="next-btn">
              {step === totalSteps ? (
                loading ? 'Submitting...' : 'Complete Registration'
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CompanyOnboarding;
